import Konva from 'konva';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Stage, Layer, Rect, Circle, Sprite, Image as KonvaImage } from 'react-konva';
import { QuestionnaireContext } from '../../../StateManagment/Questionnaire';

const playerImg = new Image();
playerImg.src = '/canvas/cyclist/cyclist_sprite_sheet.png';

const playerSpriteSize = 256;
const playerFrames = {
    cycle: [ // Row 0
        playerSpriteSize*0, 0, playerSpriteSize, playerSpriteSize,
        playerSpriteSize*1, 0, playerSpriteSize, playerSpriteSize,
        playerSpriteSize*2, 0, playerSpriteSize, playerSpriteSize,
        playerSpriteSize*3, 0, playerSpriteSize, playerSpriteSize,
        playerSpriteSize*4, 0, playerSpriteSize, playerSpriteSize,
        playerSpriteSize*5, 0, playerSpriteSize, playerSpriteSize,
        playerSpriteSize*6, 0, playerSpriteSize, playerSpriteSize,
        playerSpriteSize*7, 0, playerSpriteSize, playerSpriteSize,
    ],
    idle: [ // Row 1
        playerSpriteSize*0, playerSpriteSize*1, playerSpriteSize, playerSpriteSize,
    ],
};

const playerImgSaddlebag = new Image();
playerImgSaddlebag.src = '/canvas/cyclist/cyclist_letter_into_saddlebag_spritesheet.png';
const playerSaddlebagFrames = {
    anim0: [ // Row 0
        playerSpriteSize*0, 0, playerSpriteSize, playerSpriteSize,
        playerSpriteSize*1, 0, playerSpriteSize, playerSpriteSize,
        playerSpriteSize*2, 0, playerSpriteSize, playerSpriteSize,
        playerSpriteSize*3, 0, playerSpriteSize, playerSpriteSize,
        playerSpriteSize*4, 0, playerSpriteSize, playerSpriteSize,
        playerSpriteSize*5, 0, playerSpriteSize, playerSpriteSize,
        playerSpriteSize*6, 0, playerSpriteSize, playerSpriteSize,
    ],
};

/**
 * @param {Object} props
 * @param {boolean} props.isVisible 
 * @param {number[]} props.range - The range of the scale question, can be [0, 10] or [5-12].
 * @param {number} props.selected - The currently selected value in the scale question.
 * @param {boolean} props.overlay
 */
const ScaleGame = ({
    isVisible,
    range,
    selected,
    overlay = false
}) => {

    // Size of the player image
    const playerSize = useMemo(() => {
        return overlay 
            ? { x: 120, y: 120 } 
            : { x: 80, y: 80 }; 
    }, [overlay]);

    const { isTransitioning, setTransitionDuration } = useContext(QuestionnaireContext);

    const padStart = 1;
    const padEnd = 1;
    const rangeStart = range[0] - padStart;
    const rangeEnd = range[range.length - 1] + padEnd;
    const rangeCount = rangeEnd - rangeStart + 1; // Total number of positions

    // const rangeTotal = range[range.length - 1] - range[0] + 1; 
    const selectedNormalized = selected - rangeStart; // Normalize selected value to [0, rangeTotal]

    const ratio = selectedNormalized / rangeCount;

    const containerRef = useRef(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    /** @type {React.Ref<Konva.Sprite>} */
    const playerDynamicRef = useRef(null);
    /** @type {React.Ref<Konva.Sprite>} */
    const playerAnimatedRef = useRef(null);

    const w = dimensions.width;
    const h = dimensions.height;

    useEffect(() => {
        if (isVisible) {
            setTransitionDuration(4000);
        }
    }, [isVisible, setTransitionDuration]);

    useEffect(() => {
        if (!isVisible) return;
        if (!isTransitioning) return;
        if (!playerDynamicRef.current) return;
        if (!playerAnimatedRef.current) return;

        if (w <= 0) return;
        const playerDynamic = playerDynamicRef.current;
        const playerAnimated = playerAnimatedRef.current;

        playerAnimated.hide();
        playerDynamic.show();
        playerDynamic.x(-playerSize.x); // Start outside of screen

        /** @type {Konva.Tween[]} */
        const tweens = [];

        /**
         * @param {Konva.TweenConfig} tweenConfig 
         */
        const createTween = (tweenConfig) => {
            const tween = new Konva.Tween(tweenConfig);
            tweens.push(tween);
            tween.play();
        }

        const totalDuration = 4.0
        const startBias = 0.618; // [0, 1] Bias towards the start of the animation
        // Now calculate the start and end durations based on the bias and 'ratio'. 
        const startDuration = Math.max(1.0, totalDuration * startBias * ratio);
        const endDuration = Math.max(1.0, totalDuration * (1 - startBias) * (1 - ratio));
        const standingAnimDuration = 0.9;

        createTween({
            node: playerDynamic,
            duration: startDuration,
            x: w * ratio,
            y: 0,
            easing: Konva.Easings.EaseOut,
            onFinish: () => {
                if (!playerDynamic) return;
                if (!playerAnimated) return;

                playerDynamic.hide();
                playerAnimated.position(playerDynamic.position());
                playerAnimated.show();
                playerAnimated.frameIndex(0);
                playerAnimated.start();

                createTween({
                    node: playerAnimated,
                    duration: standingAnimDuration,
                    onFinish: () => {
                        if (!playerDynamic) return;
                        if (!playerAnimated) return;
                        
                        playerDynamic.show();
                        playerAnimated.hide();
                        createTween({
                            node: playerDynamic,
                            duration: endDuration,
                            x: w + playerSize.x, // Move outside of screen
                            easing: Konva.Easings.EaseIn,
                        });
                    }
                });
            }
        });

        playerDynamic.start();

        return () => {
            tweens.forEach(tween => {
                if (tween) {
                    tween.pause();
                }
            });
        }
    }, [isVisible, playerDynamicRef, playerAnimatedRef, ratio, w, h, isTransitioning, setTransitionDuration]);

    useEffect(() => {
        if (!isVisible) return;
        const resize = () => {
            if (containerRef.current) {
                const { clientWidth, clientHeight } = containerRef.current;
                setDimensions({ width: clientWidth, height: clientHeight });
            }
        };

        resize(); // Initial size
        window.addEventListener("resize", resize);

        return () => window.removeEventListener("resize", resize);
    }, [containerRef, isVisible]);

    if (!isVisible) {
        return null; // Don't render anything if not visible
    }

    return(
        <div ref={containerRef} style={{ 
                width: '100%', 
                height: playerSize.y, 
                margin: '0 auto', 
                pointerEvents: 'none',
                position: overlay 
                    ? 'absolute' 
                    : 'relative' 
            }}>
            <Stage 
                width={dimensions.width}
                height={dimensions.height}
                >
                <Layer name='animated' visible={isTransitioning}>
                    {/* <Rect
                        name='background-rect'
                        x={0}
                        y={0}
                        width={w}
                        height={h/2}
                        fill="lightgray"
                    /> */}
                    <Sprite
                        name="player"
                        ref={playerDynamicRef}
                        // x={w / 2}
                        // y={h * 0.9}
                        // width={playerSize.x} 
                        // height={playerSize.y}
                        scale={{
                            x: playerSize.x / playerSpriteSize, // Scale to fit the sprite sheet
                            y: playerSize.y / playerSpriteSize
                        }}
                        offset={{ 
                            x: playerSize.x/2, 
                            y: 0, //-playerSpriteSize/2
                        }}
                        image={playerImg}
                        animation="cycle"
                        animations={playerFrames}
                        frameRate={8}
                        frameIndex={0}
                    />
                    <Sprite
                        name="player-saddlebag-anim"
                        ref={playerAnimatedRef}
                        scale={{
                            x: playerSize.x / playerSpriteSize,
                            y: playerSize.y / playerSpriteSize
                        }}
                        offset={{ 
                            x: playerSize.x/2, 
                            y: 0, //-playerSpriteSize/2
                        }}
                        image={playerImgSaddlebag}
                        animation="anim0"
                        animations={playerSaddlebagFrames}
                        frameRate={8}
                        frameIndex={0}
                    />
                </Layer>
            </Stage>
        </div>
    )
}

export default ScaleGame
